import { type LocalScreenTrack, type LocalVideoTrack, DeviceChangeEvent, deviceDetector, DeviceInfo, LocalTrack, LocalTrackEvent, MediaType, RecaptureMode, TIMER_TYPE, TrackEvent, VideoPlayer } from '..';
import { VideoMixNode } from '../manager/video-node/mix';
import type {
  CameraSource,
  LayerOption,
  ScreenSource,
  TextSource,
  ImageSource,
  VideoSource
} from '../../../../plugins/video-effect/video-mixer/npm-package/video-mixer.esm';
import { TextSourceNode, VideoImageSourceNode, VideoTrackSourceNode } from '../manager/video-node/source';
import { Video2dContext } from '../manager/video-context';
import { VideoTrackDestination } from '../manager/video-node/destination';
import { isUndefined, detectImageType } from '../utils';
import { IOS_MAIN_VERSION, IS_ANY_SAFARI } from '../utils/environment';
import { VideoTransformNode } from '../manager/video-node/transform';
import { VideoNode } from '../manager/video-node/base';
import timer from '../utils/timer';
import type { VideoManager } from '../manager/video-manager';
import * as eventManager from '../manager/event-manager';

export class LocalMixVideoTrack extends LocalTrack<'video'> {
  inputLocalVideoTracks = new Map<string, LocalVideoTrack>();
  inputLocalScreenTracks = new Map<string, LocalScreenTrack>();

  cameraNodeMap = new Map<string, VideoTrackSourceNode | VideoImageSourceNode>();
  screenNodeMap = new Map<string, VideoTrackSourceNode>();
  textNodeMap = new Map<string, TextSourceNode>();
  imageNodeMap = new Map<string, VideoImageSourceNode>();
  videoNodeMap = new Map<string, VideoImageSourceNode>();

  // endedId为localVideoTrack为ended状态时所对应的trackId。用于取消对应video的requestVideoFrameCallback，避免影响到定时器渲染。
  endedIds = new Set<string>;

  // Saved non-string background reference for resize regeneration
  private _nonStringBackground: CanvasGradient | CanvasPattern | null = null;

  // Cached max fps track info to avoid full traversal on every updateRenderDriver call
  private _cachedMaxFpsTrackId: string | null = null;
  private _cachedMaxFps = 0;
  private _cachedMaxInCamera = true;
  private _cachedSourceCount = 0;
  private _fpsTrackListDirty = true;

  videoContext: Video2dContext;
  mixNode: VideoMixNode | null;
  destination: VideoTrackDestination<Video2dContext> | null;
  manager: VideoManager;
  stat: LocalVideoStat;
  private _checkId = 0;
  autoSetFps = true;
  private _workerScript?: string;
  constructor(vm: VideoManager, mediaType = MediaType.BIG_VIDEO, workerScript?: string) {
    super(mediaType, VideoPlayer, vm.room);
    // Track base class constructor does not persist the room parameter (it's only used for logger/player init).
    // LocalMixVideoTrack needs room reference for videoMixerOutputTrack identification, so assign it explicitly.
    this.room = vm.room;
    this.manager = vm;
    this._workerScript = workerScript;
    this.log.id += 'mix';
    this.create2dVideoContext();
    this.destination = this.videoContext.createVideoTrackDestination({ name: 'mainDestination2d', logger: this.log });
    this.destination.on(VideoNode.RENDER, (source: CanvasImageSource) => {
      this.emit(LocalTrackEvent.RENDER, source);
    });
    this.mixNode = new VideoMixNode(this.videoContext, this.log);
    this.mixNode.matchInputSize = false; // 合流画布大小固定
    // this.enablePrintDetail();
  }

  listenDeviceChange(): void {
    throw new Error('Method not implemented.');
  }
  enablePrintDetail(delay = 2000) {
    this._checkId = timer.run(
      TIMER_TYPE.INTERVAL,
      () => {
        if (this.destination) {
          this.log.debug(this.destination.getInfo());
        }
      },
      { delay }
    );
  }

  create2dVideoContext() {
    if (this.videoContext) {
      this.videoContext.destroy();
    } else {
      this.videoContext = new Video2dContext({ frameRate: 15, logger: this.log, name: 'mix-ctx' });
    }
    this.videoContext.create({ alpha: false, willReadFrequently: false, workerScript: this._workerScript });
  }

  setFps(frameRate: number) {
    this.autoSetFps = false;
    // 只要显式设置了帧率，就用定时器
    this.videoContext.frameRate = frameRate;

    for (const node of [...this.cameraNodeMap.values(), ...this.screenNodeMap.values()]) {
      node.shouldUpdate = false;
    }
    // Immediately switch to timer-driven mode instead of using a 500ms delay
    this.destination?.start(this.videoContext.frameRate);
  }
  /**
   * Invalidate the cached max fps info. Must be called when track list changes
   * (add/remove source, endedIds change).
   */
  private _invalidateFpsCache() {
    this._fpsTrackListDirty = true;
  }
  /**
   * Update the render driver for the mix track.
   *
   * Rendering drive chain differs by mode:
   *
   * **Main thread mode** (non-Worker):
   *   updateRenderDriver → shouldUpdate + tryVideoFrameCallback → requestVideoFrameCallback
   *   → update() → destination.requestFrame → main thread Canvas draw
   *
   * **Worker mode**:
   *   updateRenderDriver → postWorkerMessage({ type: 'setFps' })
   *   → Worker startRenderLoop() → setInterval(render, 1000/fps) → Worker Canvas draw
   *   (shouldUpdate / tryVideoFrameCallback are NOT used in Worker mode)
   */
  updateRenderDriver() {
    if (!this.autoSetFps) return;
    if (this.videoContext?.isWorkerMode) {
      this._updateRenderDriverWorker();
    } else {
      this._updateRenderDriverMainThread();
    }
  }

  /**
   * Shared fps lookup: find the camera/screen source with the highest frame rate.
   * Uses the _fpsTrackListDirty / _cachedMaxFpsTrackId cache to skip full traversal when possible.
   *
   * @param cancelEndedCallbacks - If true, call cancelVideoFrameCallback on ended tracks
   *   (only needed in main-thread mode where rVFC drives rendering).
   * @returns `{ maxId, maxFps, maxInCamera }` when a valid source is found,
   *   or `null` when no camera/screen source exists (caller should fall back to timer).
   *   Returns `'cached'` when the cache is valid and no further action is needed.
   */
  private _findMaxFpsSource(cancelEndedCallbacks: boolean): { maxId: string; maxFps: number; maxInCamera: boolean } | null | 'cached' {
    // Fast path: use cached result if track list hasn't changed
    if (!this._fpsTrackListDirty && this._cachedMaxFpsTrackId !== null) {
      // Defensive check: if source count changed, cache is stale — do full scan
      const currentSourceCount = this.inputLocalVideoTracks.size + this.inputLocalScreenTracks.size;
      if (currentSourceCount !== this._cachedSourceCount) {
        // fall through to full scan
      } else {
        // Verify cached track still exists and is not ended
        const trackMap = this._cachedMaxInCamera ? this.inputLocalVideoTracks : this.inputLocalScreenTracks;
        if (trackMap.has(this._cachedMaxFpsTrackId) && !this.endedIds.has(this._cachedMaxFpsTrackId)) {
          return 'cached';
        }
      }
      // Cache invalid, fall through to full scan
    }

    let maxId: string | null = null;
    let maxFps = 0;
    let maxInCamera = true;
    for (const [id, track] of this.inputLocalVideoTracks) {
      if (track.profile.frameRate > maxFps) {
        if (this.endedIds.has(id)) {
          if (cancelEndedCallbacks) {
            const node = this.cameraNodeMap.get(id);
            if (node) (node.image as HTMLVideoElement).cancelVideoFrameCallback(node.videoCallbackId);
          }
          continue;
        }
        maxFps = track.profile.frameRate;
        maxId = id;
      }
    }
    for (const [id, track] of this.inputLocalScreenTracks) {
      if (track.profile.frameRate > maxFps) {
        if (this.endedIds.has(id)) {
          if (cancelEndedCallbacks) {
            const node = this.screenNodeMap.get(id);
            if (node) node.image.cancelVideoFrameCallback(node.videoCallbackId);
          }
          continue;
        }
        maxFps = track.profile.frameRate;
        maxId = id;
        maxInCamera = false;
      }
    }

    // Update cache
    this._cachedMaxFpsTrackId = maxId;
    this._cachedMaxFps = maxFps;
    this._cachedMaxInCamera = maxInCamera;
    this._cachedSourceCount = this.inputLocalVideoTracks.size + this.inputLocalScreenTracks.size;
    this._fpsTrackListDirty = false;

    return maxId !== null ? { maxId, maxFps, maxInCamera } : null;
  }

  /**
   * Worker mode branch: only find max fps and sync to Worker render loop.
   * Does NOT set shouldUpdate or call tryVideoFrameCallback — Worker drives rendering
   * via its own setInterval loop.
   */
  private _updateRenderDriverWorker() {
    const result = this._findMaxFpsSource(false);

    if (result === 'cached') {
      // Cache hit — fps hasn't changed, nothing to sync
      return;
    }

    if (result !== null) {
      const { maxFps } = result;
      // Sync frameRate to videoContext and Worker render loop
      if (this.videoContext && this.videoContext.frameRate !== maxFps) {
        this.videoContext.frameRate = maxFps;
        (this.videoContext as Video2dContext).postWorkerMessage({ type: 'setFps', fps: maxFps });
      }
      this.log.info('set mix fps: ', maxFps);
    } else {
      // No camera/screen source — need Worker render loop with default frameRate.
      if (this.destination?.isWorkerDriven) {
        // Worker-driven mode is already active — just update fps and ensure timer state.
        // Do NOT call start() which would trigger full re-initialization
        // (re-sending ImageSources, re-syncing layout, etc.)
        const fps = this.videoContext.frameRate;
        (this.videoContext as Video2dContext).postWorkerMessage({ type: 'setFps', fps });
        this.destination.ensureMainThreadExtraction();
        this.log.info('no track source, Worker already active, updated fps: ', fps);
      } else {
        // Worker not active yet — perform full initialization via start()
        this.destination?.start(this.videoContext.frameRate);
        this.log.info('no track source, start worker render loop with default fps: ', this.videoContext!.frameRate);
      }
    }
  }

  /**
   * Main thread mode branch: find max fps source and use its requestVideoFrameCallback
   * as the render loop driver.
   */
  private _updateRenderDriverMainThread() {
    // Reset all shouldUpdate flags — only the highest fps source should drive rendering
    for (const node of [...this.cameraNodeMap.values(), ...this.screenNodeMap.values()]) {
      node.shouldUpdate = false;
    }

    const result = this._findMaxFpsSource(true);

    if (result === 'cached') {
      // Cache hit — re-enable the cached source's rVFC driver
      const nodeMap = this._cachedMaxInCamera ? this.cameraNodeMap : this.screenNodeMap;
      if (!this._cachedMaxFpsTrackId) return;
      const node = nodeMap.get(this._cachedMaxFpsTrackId);
      if (node) {
        node.shouldUpdate = true;
        node.tryVideoFrameCallback();
      }
      return;
    }

    if (result !== null) {
      const { maxId, maxFps, maxInCamera } = result;
      const node = maxInCamera ? this.cameraNodeMap.get(maxId) : this.screenNodeMap.get(maxId);
      if (node) {
        node.shouldUpdate = true;
        node.tryVideoFrameCallback();
      }
      // Sync frameRate to videoContext (no Worker message needed in main thread mode)
      if (this.videoContext && this.videoContext.frameRate !== maxFps) {
        this.videoContext.frameRate = maxFps;
      }
      this.log.info('set mix fps: ', maxFps);
    } else {
      // No camera/screen source — fall back to timer-driven rendering
      this.destination?.start(this.videoContext.frameRate);
      this.log.info('fallback to timer, fps: ', this.videoContext.frameRate);
    }
  }
  setMixBackground(color: string | CanvasGradient | CanvasPattern) {
    if (!this.mixNode) return;
    // Always update the main thread mixNode property (used in main thread rendering and as fallback)
    this.mixNode.backgroundColor = color;

    // Sync to Worker if in Worker mode (buffers automatically if Worker not yet ready)
    if (this.videoContext?.isWorkerMode) {
      if (typeof color !== 'string') {
        // Save non-string background reference for resize regeneration
        this._nonStringBackground = color;
        // CanvasGradient/CanvasPattern cannot be serialized via postMessage.
        // Render the gradient/pattern to a temporary canvas, convert to ImageBitmap,
        // and transfer it to the Worker as a background image.
        const w = this.mixNode.width;
        const h = this.mixNode.height;
        if (!w || !h) {
          this.log.warn('setMixBackground: mixNode width or height is 0, falling back to black background');
          this.videoContext.setWorkerBackground('black');
          return;
        }
        this._renderBackgroundToWorker(color, w, h);
      } else {
        // Clear non-string background reference when setting a string color
        this._nonStringBackground = null;
        this.videoContext.setWorkerBackground(color);
      }
    }
  }

  /**
   * Render a CanvasGradient/CanvasPattern to a temporary canvas of the given size,
   * convert to ImageBitmap, and transfer to the Worker as background image.
   */
  private _renderBackgroundToWorker(bgStyle: CanvasGradient | CanvasPattern, width: number, height: number) {
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = width;
    tmpCanvas.height = height;
    const tmpCtx = tmpCanvas.getContext('2d');
    if (tmpCtx) {
      tmpCtx.fillStyle = bgStyle;
      tmpCtx.fillRect(0, 0, width, height);
      createImageBitmap(tmpCanvas).then(bitmap => {
        this.videoContext?.setWorkerBackgroundImage(bitmap);
      })
        .catch(err => {
          this.log.warn(`_renderBackgroundToWorker: failed to create background ImageBitmap: ${(err as Error).message}`);
          this.videoContext?.setWorkerBackground('black');
        });
    } else {
      this.log.warn('_renderBackgroundToWorker: failed to create temp canvas context, falling back to black');
      this.videoContext?.setWorkerBackground('black');
    }
  }
  resizeMixCanvas(width: number, height: number) {
    this.mixNode?.resize(width, height);
    // In Worker mode, also sync size to Worker OffscreenCanvas
    // setSize buffers automatically if Worker not yet ready
    if (this.videoContext?.isWorkerMode) {
      this.videoContext.setSize(width, height);
      // Regenerate background ImageBitmap with new dimensions if a non-string background is set
      if (this._nonStringBackground && width && height) {
        this._renderBackgroundToWorker(this._nonStringBackground, width, height);
      }
    }
  }
  async startMix() {
    if (!this.mixNode || !this.destination) throw new Error('can\'t mix without necessary conditions');
    this.mixNode.disconnect();
    this.mixNode.connect(this.destination);
    // 设置outputTrack
    if (IS_ANY_SAFARI) this.player.setCanvas(this.videoContext._canvas);
    this.destination.videoTrack && this.setOutputMediaStreamTrack(this.destination.videoTrack);
    // Store VideoMixer output track reference on room for isVideoMixerOutputTrack identification
    if (this.room) this.room.videoMixerOutputTrack = this.destination.videoTrack;

    // Listen for track degradation: when TrackGenerator fails and falls back to captureStream,
    // the destination emits 'trackChanged' with the new track. We need to update our output track.
    this.destination.on('trackChanged' as any, (newTrack: MediaStreamVideoTrack) => {
      this.log.warn('startMix: destination track changed (TrackGenerator degraded), updating output track');
      this.setOutputMediaStreamTrack(newTrack);
      if (this.room) this.room.videoMixerOutputTrack = newTrack;
      this.manager?.changeInput(this);
    });

    this.manager?.changeInput(this);
  }

  addCameraSource(id: string, localVideoTrack: LocalVideoTrack, layout: LayerOption) {
    if (this.inputLocalVideoTracks.has(id) || this.cameraNodeMap.has(id)) throw new Error(`There is already a cameraSource with the same ID: ${id}`);
    const { mediaTrack } = localVideoTrack;
    if (!mediaTrack) throw new Error('no mediaTrack, add cameraSource failed');
    localVideoTrack.recaptureMode = RecaptureMode.WAIT_CURRENT_DEVICE;
    eventManager.create(this, deviceDetector!).add(DeviceChangeEvent.VIDEO_INPUT_REMOVED, (device: DeviceInfo) => {
      if (device.deviceId === localVideoTrack.deviceId) {
        this.endedIds.add(id);
        this._invalidateFpsCache();
        this.updateRenderDriver();
      }
    });
    localVideoTrack.on(TrackEvent.OUTPUT_MEDIA_TRACK_CHANGED, () => {
      this.endedIds.delete(id);
      this._invalidateFpsCache();
      this.updateCameraSource(id, layout, localVideoTrack.mediaTrack);
    });
    // 1.创建 node
    // safari 16 创建 cameraNode 的兼容代码
    let cameraNode: VideoTrackSourceNode | VideoImageSourceNode;
    if (IOS_MAIN_VERSION === 16 && mediaTrack instanceof CanvasCaptureMediaStreamTrack) {
      cameraNode = this.videoContext.createVideoImageSource(mediaTrack.canvas, {
        name: 'cameraCanvasSource',
        logger: this.log
      });
    } else cameraNode = this.videoContext.createVideoTrackSource(mediaTrack, 'cameraNodeSource');
    cameraNode.resize(localVideoTrack.settings.width, localVideoTrack.settings.height);
    cameraNode.shouldUpdate = false;
    cameraNode.sourceId = id;
    // 2.连接 node
    this._connectMix(cameraNode, layout, 'cover');
    // 连接 mix 没报错的情况下才更新 map
    this.inputLocalVideoTracks.set(id, localVideoTrack);
    this.cameraNodeMap.set(id, cameraNode);
    // 3. If Worker rendering mode is active, transfer this track's stream to Worker
    if (cameraNode instanceof VideoTrackSourceNode && this.videoContext?.isWorkerMode) {
      const streamId = this._getWorkerStreamId(cameraNode);
      if (streamId && mediaTrack) {
        this.destination?.addWorkerTrackStream(streamId, mediaTrack);
      }
    }
    this._invalidateFpsCache();
    this.updateRenderDriver();
  }
  addScreenSource(id: string, localScreenTrack: LocalScreenTrack, layout: LayerOption) {
    if (this.inputLocalScreenTracks.has(id) || this.screenNodeMap.has(id)) throw new Error(`There is already a screenSource with the same ID: ${id}`);
    const { mediaTrack } = localScreenTrack;
    if (!mediaTrack) throw new Error('no mediaTrack, add screenSource failed');
    localScreenTrack.on(TrackEvent.OUTPUT_MEDIA_TRACK_CHANGED, () => {
      this.updateScreenSource(id, layout, localScreenTrack.mediaTrack);
    });
    const screenNode = this.videoContext.createVideoTrackSource(mediaTrack, 'screenNodeSource');
    screenNode.resize(localScreenTrack.settings.width, localScreenTrack.settings.height);
    screenNode.shouldUpdate = false;
    screenNode.sourceId = id;
    this._connectMix(screenNode, layout);
    // 连接 mix 没报错的情况下才更新 map
    this.inputLocalScreenTracks.set(id, localScreenTrack);
    this.screenNodeMap.set(id, screenNode);
    // If Worker rendering mode is active, transfer this track's stream to Worker
    if (this.videoContext?.isWorkerMode) {
      const streamId = this._getWorkerStreamId(screenNode);
      if (streamId && mediaTrack) {
        this.destination?.addWorkerTrackStream(streamId, mediaTrack);
      }
    }
    this._invalidateFpsCache();
    this.updateRenderDriver();
  }
  addTextSource(textOption: TextSource) {
    const { id, content = '', font, color, layout } = textOption;
    if (this.textNodeMap.has(id)) throw new Error(`There is already a textSource with the same ID: ${id}`);
    const textNode = this.videoContext.createTextSource({
      content,
      font,
      color
    });
    textNode.resize(layout.width, layout.height);
    textNode.sourceId = id;
    this._connectMix(textNode, layout);
    this.textNodeMap.set(id, textNode);
    // In Worker mode, push the text frame on demand instead of relying on the timer
    if (this.videoContext?.isWorkerMode && this.destination) {
      const streamId = this._getWorkerStreamId(textNode);
      if (streamId) {
        this.destination.pushStaticSourceFrame(streamId, textNode);
      }
    }
    this.updateRenderDriver();
  }
  addImageSource(id: string, image: HTMLImageElement, layout: LayerOption) {
    if (this.imageNodeMap.has(id)) throw new Error(`There is already a imageSource with the same ID: ${id}`);
    const imageNode = this.videoContext.createVideoImageSource(image, { autoResize: false, logger: this.log });
    imageNode.resize(image.width, image.height);
    imageNode.sourceId = id;
    this._connectMix(imageNode, layout);
    this.imageNodeMap.set(id, imageNode);

    if (this.videoContext?.isWorkerMode) {
      // Worker mode: send src and layout to Worker, Worker handles loading/rendering internally
      const { animated, mimeType } = detectImageType(image.src);
      const streamId = this._getWorkerStreamId(imageNode);
      if (streamId) {
        (this.videoContext as Video2dContext).bufferOrPostImageSourceMessage({
          type: 'addImageSource', id: streamId, src: image.src, animated, mimeType
        });
        // Track this ImageSource ID so _resendImageSourcesToWorker can skip it
        this.destination?.trackWorkerImageSource(streamId);
      }
    } else if (this.destination) {
      // Non-Worker mode: push the image frame on demand
      const streamId = this._getWorkerStreamId(imageNode);
      if (streamId) {
        if (image.complete && image.naturalWidth > 0) {
          this.destination.pushStaticSourceFrame(streamId, imageNode);
        } else {
          image.addEventListener('load', () => {
            const currentStreamId = this._getWorkerStreamId(imageNode);
            if (currentStreamId && this.imageNodeMap.has(id)) {
              this.destination?.pushStaticSourceFrame(currentStreamId, imageNode);
            }
          }, { once: true });
        }
      }
    }
    this.updateRenderDriver();
  }
  addVideoSource(id: string, video: HTMLVideoElement, layout: LayerOption) {
    if (this.videoNodeMap.has(id)) throw new Error(`There is already a videoSource with the same ID: ${id}`);
    const videoNode = this.videoContext.createVideoImageSource(video, { autoResize: false, logger: this.log });
    videoNode.resize(video.videoWidth, video.videoHeight);
    videoNode.shouldUpdate = false;
    videoNode.sourceId = id;
    this._connectMix(videoNode, layout);
    this.videoNodeMap.set(id, videoNode);
    this.updateRenderDriver();
    // In Worker mode, ensure the main thread frame extraction timer is running for this dynamic source.
    // When updateRenderDriver() calls start() (no camera/screen sources), the timer is already created and
    // ensureMainThreadExtraction will skip (dedup via _frameExtractIntervalId check).
    // When updateRenderDriver() does NOT call start() (camera/screen exists), this explicitly creates the timer.
    if (this.videoContext?.isWorkerMode && this.destination) {
      this.destination.ensureMainThreadExtraction();
    }
  }
  updateCameraSource(
    id: string,
    layout: LayerOption,
    newMediaTrack: MediaStreamVideoTrack | null = null,
    profile?: VideoProfile
  ) {
    const mixerLocalVideoTrack = this.inputLocalVideoTracks.get(id);
    const isNewTrack = !!(mixerLocalVideoTrack && newMediaTrack && newMediaTrack !== mixerLocalVideoTrack.mediaTrack);
    if (isNewTrack) {
      this.log.debug('updateCameraSource mixerLocalVideoTrack newTrack:', newMediaTrack, 'oldTrack:', mixerLocalVideoTrack.mediaTrack);
    }
    this.log.debug(`updateCameraSource(${id}): isNewTrack=${isNewTrack}, hasNewMediaTrack=${!!newMediaTrack}, hasProfile=${!!profile}, hasCameraNode=${this.cameraNodeMap.has(id)}`);
    // 传入 profile 说明摄像头更新了采集参数
    let cameraNode = this.cameraNodeMap.get(id);
    if (cameraNode) {
      if (newMediaTrack) {
        // Skip track replacement if this is a re-entrant call (isNewTrack=false means
        // newMediaTrack === mixerLocalVideoTrack.mediaTrack, i.e. already processed).
        // Re-replacing the same track would create a second MediaStreamTrackProcessor,
        // causing the Worker pipeline to receive no frames.
        if (!isNewTrack) {
          this.log.debug(`updateCameraSource(${id}): skipped track replacement (re-entrant call, track already set)`);
        } else if (IOS_MAIN_VERSION === 16 && newMediaTrack instanceof CanvasCaptureMediaStreamTrack) {
          if (cameraNode instanceof VideoTrackSourceNode) {
            // 只销毁旧 cameraNode，不销毁 transform，transform 不会与 mix 断连，layout 也不会丢失
            const transformNode = cameraNode.output;
            // Remove old Worker stream before closing node
            this._removeWorkerStreamForNode(cameraNode);
            cameraNode.close();
            cameraNode = this.videoContext.createVideoImageSource(newMediaTrack.canvas, {
              name: 'cameraCanvasSource',
              logger: this.log
            });
            transformNode && cameraNode.connect(transformNode); // 重新连接 transform 和 mix
            this.cameraNodeMap.set(id, cameraNode); // 更新 map
          } else {
            cameraNode.image = newMediaTrack.canvas;
          }
        } else {
          if (cameraNode instanceof VideoTrackSourceNode) {
            // IMPORTANT: Order matters. First activate the track via replaceTrack(), which sets
            // the track on the internal <video>.srcObject. MediaStreamTrackProcessor requires
            // the track to be consumed by a <video> element before it can read frames.
            // Then call _replaceWorkerStreamForNode() which creates the MediaStreamTrackProcessor.
            // Reversing this order causes the processor to receive no frames in Worker mode.
            this.log.debug(`updateCameraSource(${id}): replacing cameraNode track and Worker stream, isWorkerMode=${this.videoContext?.isWorkerMode}`);
            cameraNode.replaceTrack(newMediaTrack);
            this._replaceWorkerStreamForNode(cameraNode, newMediaTrack);
          } else {
            const transformNode = cameraNode.output;
            cameraNode.close();
            cameraNode = this.videoContext.createVideoTrackSource(newMediaTrack, 'cameraNodeSource');
            transformNode && cameraNode.connect(transformNode); // 重新连接 transform 和 mix
            this.cameraNodeMap.set(id, cameraNode); // 更新 map
          }
        }

        const { width, height } = newMediaTrack.getSettings();
        if (width && height) cameraNode.resize(width, height);
      }
      if (profile) {
        // 更新了采集参数还需要 resize node，以及重新计算帧率
        cameraNode.resize(profile.width, profile.height);
      }
      if (profile || newMediaTrack) this.updateRenderDriver();
      this._changeMixLayout(cameraNode, layout);
    }
    // IMPORTANT: Defer setInputMediaStreamTrack to AFTER all cameraNode/Worker operations.
    // Calling it triggers a synchronous re-entry chain:
    //   setInputMediaStreamTrack → changeInput → setCameraTrack → update() → _setMainOutput()
    //   → innerEmitter.emit(LOCAL_VIDEO_TRACK_PREPROCESSED) → preprocessedListener
    //   → updateCameraSource (re-entrant call)
    // By deferring to the end, the re-entrant call sees isNewTrack=false (track already set),
    // so it naturally skips replaceTrack and _replaceWorkerStreamForNode.
    if (isNewTrack) {
      mixerLocalVideoTrack.setInputMediaStreamTrack(newMediaTrack);
    }
  }
  updateScreenSource(id: string, layout: LayerOption, newMediaTrack: MediaStreamVideoTrack | null = null) {
    const mixerLocalScreenTrack = this.inputLocalScreenTracks.get(id);
    const isNewTrack = !!(mixerLocalScreenTrack && newMediaTrack && newMediaTrack !== mixerLocalScreenTrack.mediaTrack);
    this.log.debug(`updateScreenSource(${id}): isNewTrack=${isNewTrack}, hasNewMediaTrack=${!!newMediaTrack}`);
    const screenNode = this.screenNodeMap.get(id);
    if (screenNode) {
      if (newMediaTrack) {
        if (!isNewTrack) {
          this.log.debug(`updateScreenSource(${id}): skipped track replacement (re-entrant call, track already set)`);
        } else {
          // Replace Worker stream if applicable
          this._replaceWorkerStreamForNode(screenNode, newMediaTrack);
          screenNode.replaceTrack(newMediaTrack);
        }
      }
      this._changeMixLayout(screenNode, layout);
    }
    // Update mixerLocalScreenTrack AFTER screenNode and Worker stream operations.
    // setInputMediaStreamTrack synchronously triggers OUTPUT_MEDIA_TRACK_CHANGED,
    // which re-enters updateScreenSource via the listener in addScreenSource.
    // By deferring it to the end, the re-entrant call sees
    // newMediaTrack === mixerLocalScreenTrack.mediaTrack (isNewTrack=false),
    // so it naturally skips the heavy _replaceWorkerStreamForNode operations.
    if (isNewTrack) {
      mixerLocalScreenTrack.setInputMediaStreamTrack(newMediaTrack);
    }
  }
  updateTextSource(textOption: TextSource) {
    const { id, content, font, color, layout } = textOption;
    const textNode = this.textNodeMap.get(id);
    if (textNode) {
      // Track whether content has actually changed (need to push new frame)
      let contentChanged = false;
      if (!isUndefined(content) && content !== textNode.content) {
        textNode.content = content;
        contentChanged = true;
      }
      if (!isUndefined(font) && font !== textNode.font) {
        textNode.font = font;
        contentChanged = true;
      }
      if (!isUndefined(color) && color !== textNode.color) {
        textNode.color = color;
        contentChanged = true;
      }
      textNode.resize(layout.width, layout.height); // textNode 画布宽高始终与 layout 保持一致
      // In Worker mode, push the updated text frame on demand when content changed
      if (contentChanged && this.videoContext?.isWorkerMode && this.destination) {
        // Trigger re-render of text canvas before pushing
        textNode.requestFrame(0);
        const streamId = this._getWorkerStreamId(textNode);
        if (streamId) {
          this.destination.pushStaticSourceFrame(streamId, textNode);
        }
      }
      this._changeMixLayout(textNode, layout);
    }
  }
  updateImageSource(id: string, layout: LayerOption, imageElement?: HTMLImageElement) {
    const imageNode = this.imageNodeMap.get(id);
    if (imageNode) {
      if (imageElement) {
        imageNode.image = imageElement;
        imageNode.resize(imageElement.width, imageElement.height);

        if (this.videoContext?.isWorkerMode) {
          // Worker mode: send updated src to Worker, Worker handles reloading internally
          const { animated, mimeType } = detectImageType(imageElement.src);
          const streamId = this._getWorkerStreamId(imageNode);
          if (streamId) {
            (this.videoContext as Video2dContext).bufferOrPostImageSourceMessage({
              type: 'updateImageSource', id: streamId, src: imageElement.src, animated, mimeType
            });
          }
        } else if (this.destination) {
          // Non-Worker mode: push the updated image frame on demand
          const streamId = this._getWorkerStreamId(imageNode);
          if (streamId) {
            if (imageElement.complete && imageElement.naturalWidth > 0) {
              this.destination.pushStaticSourceFrame(streamId, imageNode);
            } else {
              imageElement.addEventListener('load', () => {
                const currentStreamId = this._getWorkerStreamId(imageNode);
                if (currentStreamId && this.imageNodeMap.has(id)) {
                  this.destination?.pushStaticSourceFrame(currentStreamId, imageNode);
                }
              }, { once: true });
            }
          }
        }
      }
      this._changeMixLayout(imageNode, layout);
    }
  }
  updateVideoSource(id: string, layout: LayerOption, videoElement?: HTMLVideoElement) {
    const node = this.videoNodeMap.get(id);
    if (node) {
      if (videoElement) {
        const oldImage = node.image;
        if (oldImage instanceof HTMLVideoElement) {
          // 释放旧视频资源
          this.stopVideoElement(oldImage);
        }
        node.image = videoElement;
        node.resize(videoElement.videoWidth, videoElement.videoHeight);
      }
      this._changeMixLayout(node, layout);
    }
  }
  private _connectMix<S extends TexImageSource>(
    node: VideoNode<S>,
    layout: LayerOption,
    defaultFillMode: 'contain' | 'cover' | 'fill' = 'contain'
  ) {
    if (!this.mixNode) return;
    // 始终连接 transform 节点，避免 node -> transform -> mix 和 node -> mix 来回切换，从而避免繁琐的 mix layout 信息同步逻辑
    const { mirror, rotation } = layout;
    node.disconnect();
    let lastNode = new VideoTransformNode(this.videoContext as Video2dContext, this.log, mirror, rotation);
    lastNode = node.connect(lastNode);
    // Propagate sourceId from source node to transform node so that
    // VideoMixNode.addInput can read it as a stable stream ID.
    lastNode.sourceId = node.sourceId;

    if (!layout.fillMode) layout.fillMode = defaultFillMode;
    lastNode.connect(this.mixNode, layout);

    // In Worker mode, immediately sync the latest layout to Worker (don't wait for next timer tick).
    // Timer management (ensureMainThreadExtraction) is NOT called here to avoid creating a timer
    // that gets immediately destroyed and recreated by updateRenderDriver() → start() → _startFrameExtraction().
    // Instead, each addXxxSource method handles timer evaluation after updateRenderDriver() completes.
    if (this.videoContext?.isWorkerMode && this.destination) {
      this.destination.syncLayoutToWorker();
    }
  }

  private _changeMixLayout<S extends TexImageSource>(node: VideoNode<S>, layout: LayerOption) {
    if (!this.mixNode) return;
    // 1.镜像、旋转逻辑
    const { mirror, rotation } = layout;
    const lastNode = node.output || node;
    if (lastNode instanceof VideoTransformNode) {
      if (!isUndefined(mirror)) lastNode.mirror = mirror;
      if (!isUndefined(rotation)) lastNode.rotation = rotation;
      lastNode.resize(node.width, node.height); // 触发 transform 的 resize 逻辑，会自动根据旋转角度交换宽高
    }

    // 2.修改 layout
    this.mixNode.changeInputLayout(lastNode, layout);

    // 3. In Worker mode, immediately sync the updated layout to Worker
    if (this.videoContext?.isWorkerMode && this.destination) {
      this.destination.syncLayoutToWorker();
    }
  }

  removeCameraSource(id: string) {
    const localVideoTrack = this.inputLocalVideoTracks.get(id);
    if (!localVideoTrack) return;
    // 释放 track
    localVideoTrack.close();
    this.inputLocalVideoTracks.delete(id);
    // 释放 node
    const oldNode = this.cameraNodeMap.get(id);
    if (oldNode) {
      // Remove Worker stream if transferred
      this._removeWorkerStreamForNode(oldNode);
      // close 会断开和 mixNode 的连接
      if (oldNode.output instanceof VideoTransformNode) oldNode.output.close();
      oldNode.close();
      this.cameraNodeMap.delete(id);
    }
    this.checkAfterRemove(true);
  }
  removeScreenSource(id: string) {
    const localScreenTrack = this.inputLocalScreenTracks.get(id);
    if (!localScreenTrack) return;
    localScreenTrack.close();
    this.inputLocalScreenTracks.delete(id);
    const oldNode = this.screenNodeMap.get(id);
    if (oldNode) {
      // Remove Worker stream if transferred
      this._removeWorkerStreamForNode(oldNode);
      if (oldNode.output instanceof VideoTransformNode) oldNode.output.close();
      oldNode.close();
      this.screenNodeMap.delete(id);
    }
    this.checkAfterRemove(true);
  }
  removeTextSource(id: string) {
    const oldNode = this.textNodeMap.get(id);
    if (oldNode) {
      if (oldNode.output instanceof VideoTransformNode) oldNode.output.close();
      oldNode.close();
      this.textNodeMap.delete(id);
    }
    this.checkAfterRemove();
  }
  removeImageSource(id: string) {
    const oldNode = this.imageNodeMap.get(id);
    if (oldNode) {
      // In Worker mode, notify Worker to clean up image source resources
      if (this.videoContext?.isWorkerMode && this.mixNode) {
        const transformNode = oldNode.output;
        if (transformNode) {
          const mixInput = this.mixNode.inputs.find(input => input.node === transformNode);
          if (mixInput) {
            const streamId = mixInput.node.sourceId;
            (this.videoContext as Video2dContext).bufferOrPostImageSourceMessage({ type: 'removeImageSource', id: streamId });
            // Untrack this ImageSource ID so _resendImageSourcesToWorker can re-send it if needed
            this.destination?.untrackWorkerImageSource(streamId);
          }
        }
      }
      if (oldNode.output instanceof VideoTransformNode) oldNode.output.close();
      oldNode.close();
      this.imageNodeMap.delete(id);
    }
    this.checkAfterRemove();
  }
  removeVideoSource(id: string) {
    const oldNode = this.videoNodeMap.get(id);
    if (oldNode) {
      if (oldNode.output instanceof VideoTransformNode) oldNode.output.close();
      if (oldNode.image instanceof HTMLVideoElement) this.stopVideoElement(oldNode.image);
      oldNode.close();
      this.videoNodeMap.delete(id);
    }
    this.checkAfterRemove();
  }

  checkAfterRemove(refreshFps = false) {
    // 移除节点后共同的检查逻辑
    this._invalidateFpsCache();
    if (refreshFps) this.updateRenderDriver();

    // In Worker mode, re-evaluate whether the main thread frame extraction timer
    // should still be running (stop it if all non-Track sources have been removed).
    // Also sync the updated layout immediately so Worker reflects the removal.
    if (this.videoContext?.isWorkerMode && this.destination) {
      this.destination.syncLayoutToWorker();
      this.destination.ensureMainThreadExtraction();
    }
  }
  stopVideoElement(video: HTMLVideoElement) {
    video.pause(); // 不是所有浏览器都完全支持 srcObject，因此调用 pause 作为兜底
    video.src = '';
    video.srcObject = null;
    video.remove();
  }

  // ===================== Worker Stream Helpers =====================

  /**
   * Get the Worker stream ID for a source node based on its transform node and mix layout.
   * Uses the stable ID from NodeMixInfo which is derived from the user-provided source id.
   */
  private _getWorkerStreamId(sourceNode: VideoNode<TexImageSource>): string | null {
    const transformNode = sourceNode.output;
    if (!transformNode || !this.mixNode) return null;
    const mixInput = this.mixNode.inputs.find(input => input.node === transformNode);
    if (!mixInput) return null;
    return mixInput.node.sourceId;
  }

  /**
   * Remove the Worker stream for a source node (if transferred).
   */
  private _removeWorkerStreamForNode(sourceNode: VideoNode<TexImageSource>) {
    if (!this.videoContext?.isWorkerMode) return;
    const transformNode = sourceNode.output;
    if (!transformNode || !this.mixNode) return;
    const mixInput = this.mixNode.inputs.find(input => input.node === transformNode);
    if (!mixInput) return;
    const streamId = mixInput.node.sourceId;
    this.destination?.removeWorkerTrackStream(streamId);
  }

  /**
   * Replace the Worker stream for a source node with a new track.
   */
  private _replaceWorkerStreamForNode(sourceNode: VideoNode<TexImageSource>, newTrack: MediaStreamTrack) {
    if (!this.videoContext?.isWorkerMode) {
      return;
    }
    const transformNode = sourceNode.output;
    if (!transformNode || !this.mixNode) {
      return;
    }
    const mixInput = this.mixNode.inputs.find(input => input.node === transformNode);
    if (!mixInput) {
      return;
    }
    const streamId = mixInput.node.sourceId;
    const isTransferred = this.videoContext?.isStreamTransferred(streamId);
    this.log.debug(`_replaceWorkerStreamForNode: streamId=${streamId}, isTransferred=${isTransferred}, newTrack.id=${newTrack.id}, newTrack.label=${newTrack.label}, newTrack.readyState=${newTrack.readyState}`);
    if (isTransferred) {
      this.destination?.replaceWorkerTrackStream(streamId, newTrack);
    }
  }
  close() {
    super.close();
    if (this.room) this.room.videoMixerOutputTrack = null;
    timer.clearTask(this._checkId);
    this.videoContext?.destroy(); // 会发送 disconnect 事件，从而触发所有 context 下的节点关闭
    this.mixNode = null;
    this.destination = null;
    for (const track of [...this.inputLocalVideoTracks.values(), ...this.inputLocalScreenTracks.values()]) {
      track.close();
    }
    this.inputLocalVideoTracks.clear();
    this.inputLocalScreenTracks.clear();
    this.cameraNodeMap.clear();
    this.screenNodeMap.clear();
    this.textNodeMap.clear();
    this.imageNodeMap.clear();

    eventManager.remove(this);

    for (const node of this.videoNodeMap.values()) {
      // 释放视频资源
      if (node.image instanceof HTMLVideoElement) this.stopVideoElement(node.image);
    }
    this.videoNodeMap.clear();
    this.log.info('localMixVideoTrack close, stop mix');
  }
}
